import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ludsElevation } from '../../shared/styles/elevation';

import { classMap } from 'lit/directives/class-map.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { rotate } from '../../shared/styles/animations';
import { ludsBaseFont } from '../../shared/styles/typography';
import { utilsClasses } from '../../shared/styles/utils';
import { KeyboardKeyCodes } from '../../shared/types/common.model';
import '../button/button';
import '../dropodown-menu/dropdown-menu';
import '../icon/icon';

/**
 * LudsDropdownMenu component
 */
export interface SelectOption {
  id: string | number;
  title: string;
}

@customElement('luds-select')
export class LudsSelect extends LitElement {
  static override styles = [
    utilsClasses,
    ludsBaseFont,
    ludsElevation,
    rotate,
    css`
      :host {
        --fs-14: 14px;
        --fw-600: 600;
        --lh-16: 16px;
        --disable-input-field-opacity: 0.42;

        display: inline-block;
        width: 100%;
      }

      .label {
        display: inline-block;
        margin-bottom: var(--spacing-spacing-04);
        color: var(--semantic-colour-tokens-text-text-primary, #0a1826);
        font-size: var(--fs-14);
        font-weight: var(--fw-600);
        line-height: var(--lh-16);
      }

      .input-field {
        padding: var(--spacing-spacing-08) var(--spacing-spacing-12);
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--spacing-spacing-08);
        border-radius: var(--corners-s);
        border-width: 1px;
        border-style: solid;
        border-color: var(--semantic-colour-tokens-stroke-stroke-primary);
        background: var(--semantic-colour-tokens-surface-surface-primary);

        &:hover {
          cursor: pointer;
          border-color: var(--semantic-colour-tokens-stroke-stroke-dark);
        }

        &:focus {
          outline: none;
          border-color: var(--theme-link-color, --colours-brand-primary);
          box-shadow: var(--luds-elevetion-5);
        }
      }

      .disabled .input-field {
        &:hover {
          cursor: not-allowed;
          border-color: var(--semantic-colour-tokens-stroke-stroke-primary);
        }

        &:focus {
          box-shadow: none;
          border-color: var(--semantic-colour-tokens-stroke-stroke-primary);
        }
      }

      .disabled.input-field-wrapper {
        opacity: var(--disable-input-field-opacity);
      }

      .input-field-wrapper {
        position: relative;
      }

      span {
        user-select: none;
      }

      .input-icon {
        transform: rotate(0deg);
        transition: transform;
      }

      .input-icon.rotated {
        transform: rotate(180deg);
      }
    `,
  ];

  @property({ type: String }) label: string;
  @property({ type: Boolean }) hideLabel = false;
  @property({ type: String }) placeholder: string;
  @property({ type: Boolean }) disabled = false;

  @state() isOpen = false;
  @state() selected: { id: string | number; title: string } | null = null;

  @property({ type: Array }) options: SelectOption[] = [];

  private handleSelectedItem(event: CustomEvent) {
    this.selected = event.detail.item;
    this.isOpen = false;
  }

  override render() {
    const { hideLabel, disabled } = this;
    const dropdownMenuRef = createRef();

    const toggleMenu = (event: Event) => {
      event.preventDefault();
      if (this.disabled) return;

      this.isOpen = !this.isOpen;
    };

    const inputIconClasses = {
      'input-icon': true,
      rotated: this.isOpen,
    };

    return html`
      <div class="input-field-wrapper ${disabled ? 'disabled' : ''}">
        <label id="single-select-label" class="label ${hideLabel ? 'sr-only' : ''}">${this.label}</label>
        <div
          class="input-field"
          id="single-select-input"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded="${this.isOpen}"
          aria-controls="single-dropdown-menu"
          aria-labelledby="single-select-label"
          aria-disabled="${disabled}"
          tabindex="0"
          @click="${(e: MouseEvent) => toggleMenu(e)}"
          @keydown="${(e: KeyboardEvent) =>
            (e.keyCode === KeyboardKeyCodes.Enter || e.keyCode === KeyboardKeyCodes.Space) && toggleMenu(e)}">
          <span>${this.selected ? this.selected.title : this.placeholder}</span>
          <luds-icon class="${classMap(inputIconClasses)}" icon="chevron_down" aria-hidden="true"></luds-icon>
        </div>

        <luds-dropdown-menu
          ${ref(dropdownMenuRef)}
          id="single-dropdown-menu"
          .isOpen="${this.isOpen}"
          .dropdownMenuItems=${this.options}
          .isSelectDropdownMenu=${true}
          @dropdown-item-action="${this.handleSelectedItem}"
          displayAs="title"
          a11yDropdownAriaLabel="${this.label}"></luds-dropdown-menu>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'luds-select': LudsSelect;
  }
}
